import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AlertCircle, Check, Clock, Download, EyeOff, Link2, Loader2, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { LogoMark } from 'components/global/LogoMark';
import config from 'config';
import * as AttemptModule from 'modules/attempt';
import { RenderedHtml, htmlIsEmpty } from 'modules/editor';
import { ScaledReportCard } from 'modules/standardizedExam/components/report/ScaledReportCard';
import { useTranslation } from 'react-i18next';
import RedirectNotice from './RedirectNotice';

type ResultData = AttemptModule.Types.IApi.GuestResult.Response;

/** The exam ground; the result card sits 56px from the top on desktop. */
const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-[#F4F5F7] px-4 py-4 dark:bg-background sm:px-6 sm:pb-10 sm:pt-14">{children}</div>
);

/** A short message in the same card language: loading, not found, pending, hidden. */
const MessageCard: React.FC<{ icon?: React.ReactNode; title: string; children?: React.ReactNode }> = ({ icon, title, children }) => (
    <div className="mx-auto w-full max-w-[448px]">
        <a
            href={config.landing_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-4 inline-flex items-center gap-2.5 rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="Examing"
        >
            <LogoMark size={26} className="rounded-[7px]" />
            <span className="font-display text-sm font-extrabold tracking-[-0.02em] text-foreground/80">Examing</span>
        </a>
        <article className="rounded-xl border border-border bg-card p-6 sm:p-7">
            {icon && <span className="mb-3 flex text-muted-foreground">{icon}</span>}
            <h1 className="text-xl font-bold leading-[1.25] tracking-[-0.02em] text-foreground">{title}</h1>
            {children}
        </article>
    </div>
);

const GuestResultPage: React.FC = () => {
    const { t } = useTranslation();
    const { resultToken } = useParams<{ resultToken: string }>();
    // Only the submit that lands here carries this; reopening the result link must not bounce the respondent away.
    const justFinished = (useLocation().state as { justFinished?: boolean } | null)?.justFinished === true;

    const [result, setResult] = useState<ResultData | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);
    const [requestingReview, setRequestingReview] = useState(false);
    const [reviewFailed, setReviewFailed] = useState(false);
    const [confirmingReview, setConfirmingReview] = useState(false);

    const load = useCallback(async () => {
        if (!resultToken) return;
        setLoading(true);
        try {
            const { data } = await AttemptModule.Api.GuestResult(resultToken);
            setResult(data);
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [resultToken]);

    useEffect(() => {
        load();
    }, [load]);

    // A quiet re-read: the page keeps showing the report instead of falling back to the full-page loader.
    const refresh = useCallback(async () => {
        if (!resultToken) return;
        setRefreshing(true);
        try {
            const { data } = await AttemptModule.Api.GuestResult(resultToken);
            setResult(data);
        } catch {
            /* keep the report on screen; the refresh button stays available */
        } finally {
            setRefreshing(false);
        }
    }, [resultToken]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard blocked — no-op */
        }
    };

    const requestReview = async () => {
        if (!resultToken) return;
        setRequestingReview(true);
        setReviewFailed(false);
        try {
            await AttemptModule.Api.RequestReview(resultToken);
            await load();
        } catch {
            setReviewFailed(true);
        } finally {
            setRequestingReview(false);
            setConfirmingReview(false);
        }
    };

    const downloadPdf = () => {
        if (resultToken) window.open(AttemptModule.Api.guestResultPdfUrl(resultToken), '_blank');
    };

    if (loading) {
        return (
            <Page>
                <div className="flex flex-col items-center gap-3 py-24" role="status">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
                    <p className="text-sm text-muted-foreground">{t('loading')}</p>
                </div>
            </Page>
        );
    }

    if (notFound || !result) {
        return (
            <Page>
                <MessageCard icon={<AlertCircle className="h-5 w-5" aria-hidden />} title={t('result_not_found')} />
            </Page>
        );
    }

    const redirectUrl = justFinished ? result.redirectUrl : null;

    // Hidden / pending states: confirmation only, no score. The author's end message still applies.
    if (result.visibility !== 'shown') {
        const pending = result.visibility === 'pending';
        return (
            <Page>
                <MessageCard
                    icon={pending ? <Clock className="h-5 w-5" aria-hidden /> : <EyeOff className="h-5 w-5" aria-hidden />}
                    title={result.testTitle}
                >
                    <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                        {pending
                            ? result.reviewRequested
                                ? t('result_review_in_progress')
                                : t('your_results_are_pending')
                            : t('your_response_has_been_submitted')}
                    </p>
                    {!htmlIsEmpty(result.endMessage) && (
                        <div
                            data-testid="end-message"
                            className="mt-4 space-y-2 border-t border-border pt-4 text-[15px] leading-relaxed text-foreground"
                        >
                            <RenderedHtml html={result.endMessage ?? ''} />
                        </div>
                    )}
                </MessageCard>
                {redirectUrl && <RedirectNotice url={redirectUrl} className="mx-auto mt-4 w-full max-w-[448px]" />}
                {/* IELTS: Listening/Reading bands are ready while Writing waits for a teacher. */}
                {pending && result.scaledReport && (
                    <div className="mx-auto mt-4 w-full max-w-[448px]">
                        <ScaledReportCard compact report={result.scaledReport} onRefresh={refresh} refreshing={refreshing} />
                    </div>
                )}
            </Page>
        );
    }

    return (
        <Page>
            {result.scaledReport && (
                <div className="mx-auto mb-4 w-full max-w-[760px]">
                    <ScaledReportCard compact report={result.scaledReport} onRefresh={refresh} refreshing={refreshing} />
                </div>
            )}
            <AttemptModule.TestResultSummary
                data={result}
                actions={
                    <>
                        <Button variant="outline" className="h-11 w-full gap-2 sm:h-10 sm:w-auto" onClick={copyLink}>
                            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
                            {copied ? t('copied') : t('copy_result_link')}
                        </Button>
                        <Button className="h-11 w-full gap-2 sm:h-10 sm:w-auto" onClick={downloadPdf}>
                            <Download className="h-4 w-4" aria-hidden />
                            {t('export_pdf')}
                        </Button>
                    </>
                }
            />
            {redirectUrl && <RedirectNotice url={redirectUrl} className="mx-auto mt-4 w-full max-w-[760px]" />}
            {result.aiGraded && (
                <aside className="mx-auto mt-4 flex w-full max-w-[760px] flex-col gap-3 rounded-xl border border-border bg-card px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
                    <p className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        <span>
                            {t('result_ai_graded_notice')}
                            {reviewFailed && (
                                <span role="alert" className="mt-1 block text-destructive">
                                    {t('request_review_failed')}
                                </span>
                            )}
                        </span>
                    </p>
                    {confirmingReview ? (
                        <div className="flex flex-col gap-3 sm:max-w-[300px]">
                            <p className="text-sm font-medium leading-relaxed text-foreground">{t('request_review_warning')}</p>
                            <div className="flex gap-2">
                                <Button className="h-11 flex-1 sm:h-10" onClick={requestReview} disabled={requestingReview}>
                                    {t('request_review_confirm')}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="h-11 flex-1 sm:h-10"
                                    onClick={() => setConfirmingReview(false)}
                                    disabled={requestingReview}
                                >
                                    {t('cancel')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            className="h-11 w-full shrink-0 sm:h-10 sm:w-auto"
                            onClick={() => setConfirmingReview(true)}
                        >
                            {t('request_teacher_review')}
                        </Button>
                    )}
                </aside>
            )}
        </Page>
    );
};

export default GuestResultPage;
